import { dowJonesEvidence } from './dowJonesEvidenceData';
import './neo-office.css';

export default function DowJonesEvidence({
  data,
  profileId,
}: {
  data: unknown;
  profileId?: string | null;
}) {
  const evidence = dowJonesEvidence(data);
  if (!evidence.isSip && !evidence.isOol) return null;
  const hasDetails =
    evidence.comments.length +
      evidence.allegations.length +
      evidence.sources.length +
      evidence.officialLists.length >
    0;
  return (
    <section className="dj-evidence" aria-label="Dow Jones review evidence">
      <h3>
        {[
          evidence.isSip && 'Adverse Media · Special Interest Person (SIP)',
          evidence.isOol && 'Other Official Lists (OOL)',
        ]
          .filter(Boolean)
          .join(' · ')}
      </h3>
      {evidence.mediaReviewPaused && (
        <p className="dj-coverage" role="status">
          SIP/OOL review is paused pending approved rules, OOL list selection
          and testing. This is not a false-positive decision. Original evidence
          is preserved below; PEP and sanctions reviews continue separately.
        </p>
      )}
      {evidence.mediaIdentityAutoDismissed && (
        <p className="dj-coverage">
          The OOL/SIP identity match was automatically dismissed. See the match
          comment for the reason. Original provider evidence is preserved below;
          any remaining PEP or sanctions review is handled separately.
        </p>
      )}
      <p>
        Review the customer’s identity and the source context before making a
        decision.
      </p>
      {evidence.isSip && (
        <p className="dj-coverage">
          Included coverage: SIP within Watchlist. The separate Adverse Media
          Entities dataset is not included.
        </p>
      )}
      {evidence.isOol && (
        <p className="dj-coverage">
          Other Official Lists within Watchlist. An OOL listing requires review
          and does not by itself confirm a sanctions match.
        </p>
      )}
      {evidence.listNotice && <p>{evidence.listNotice}</p>}
      {evidence.officialLists.map((item, i) => (
        <article key={`ool-${i}`}>
          <h4>{item.name}</h4>
          {item.description && (
            <p className="dj-source-text">{item.description}</p>
          )}
          {(item.fromDate || item.toDate) && (
            <p>
              Listed: {item.fromDate || 'Date unavailable'}
              {item.toDate && ` · To: ${item.toDate}`}
            </p>
          )}
          {item.sources.length > 0 && (
            <ul>
              {item.sources.map((source, j) => (
                <li key={j}>
                  {source.url ? (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {source.title} ↗
                    </a>
                  ) : (
                    source.title
                  )}
                </li>
              ))}
            </ul>
          )}
        </article>
      ))}
      {evidence.categories.length > 0 && (
        <p>
          <strong>Watchlist categories:</strong>{' '}
          {evidence.categories.join(' · ')}
        </p>
      )}
      {evidence.notice && <p>{evidence.notice}</p>}
      {evidence.comments.map((comment, i) => (
        <p className="dj-source-text" key={i}>
          {comment}
        </p>
      ))}
      {evidence.allegations.map((allegation, i) => (
        <article key={i}>
          <h4>
            {allegation.descriptions.join(' · ') || 'Reported allegation'}
          </h4>
          {allegation.sources.map((source, j) => (
            <div key={j}>
              <p>
                <strong>{source.name || 'Source details'}</strong>
                {source.date && ` · ${source.date}`}
              </p>
              {source.snippet && (
                <p className="dj-source-text">{source.snippet}</p>
              )}
              {source.reference && (
                <small>Source reference: {source.reference}</small>
              )}
            </div>
          ))}
        </article>
      ))}
      {evidence.sources.length > 0 && (
        <>
          <h4>Sources</h4>
          <ul>
            {evidence.sources.map((source, i) => (
              <li key={i}>
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {source.title} ↗
                  </a>
                ) : (
                  source.title
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {!hasDetails && (
        <p>
          Source details were not returned by Dow Jones for this saved result.
        </p>
      )}
      {profileId && (
        <p>
          <a
            href={`https://eu.riskcenter.dowjones.com/search/profile?id=${encodeURIComponent(
              profileId
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open profile in Dow Jones ↗
          </a>
        </p>
      )}
    </section>
  );
}
